import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pipelineAPI, cvAPI, jobMatchAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Briefcase, ChevronLeft, Plus, Trash2, Calendar, Building, 
  MapPin, CheckCircle, Clock, XCircle, AlertCircle, Users,
  Phone, Mail, Linkedin, Edit2, Eye, TrendingUp, Bell, Target,
  ChevronRight, ExternalLink, Star, MoreVertical, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  saved: { label: 'Saved', icon: <Star className="w-4 h-4" />, color: 'bg-slate-100 text-slate-700', order: 0 },
  applied: { label: 'Applied', icon: <Clock className="w-4 h-4" />, color: 'bg-blue-100 text-blue-700', order: 1 },
  phone_screen: { label: 'Phone Screen', icon: <Phone className="w-4 h-4" />, color: 'bg-cyan-100 text-cyan-700', order: 2 },
  interview: { label: 'Interview', icon: <Users className="w-4 h-4" />, color: 'bg-amber-100 text-amber-700', order: 3 },
  offer: { label: 'Offer', icon: <CheckCircle className="w-4 h-4" />, color: 'bg-emerald-100 text-emerald-700', order: 4 },
  accepted: { label: 'Accepted', icon: <CheckCircle className="w-4 h-4" />, color: 'bg-green-100 text-green-700', order: 5 },
  rejected: { label: 'Rejected', icon: <XCircle className="w-4 h-4" />, color: 'bg-red-100 text-red-700', order: 6 },
  withdrawn: { label: 'Withdrawn', icon: <XCircle className="w-4 h-4" />, color: 'bg-gray-100 text-gray-700', order: 7 }
};

const priorityConfig = {
  high: { label: 'High', color: 'bg-red-100 text-red-700' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700' }
};

const JobPipelinePage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [cvs, setCVs] = useState([]);
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailJob, setDetailJob] = useState(null);
  const [activeTab, setActiveTab] = useState('pipeline');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [newJob, setNewJob] = useState({
    company: '',
    position: '',
    location: '',
    salary_range: '',
    job_url: '',
    job_description: '',
    status: 'saved',
    priority: 'medium',
    notes: '',
    cv_used: ''
  });

  const [newContact, setNewContact] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    linkedin: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []); // Run once on mount

  const loadData = async () => {
    try {
      const [jobsRes, cvsRes, statsRes, followupsRes] = await Promise.all([
        pipelineAPI.list(),
        cvAPI.list(),
        pipelineAPI.getStats(),
        pipelineAPI.getFollowups()
      ]);
      setJobs(jobsRes.data);
      setCVs(cvsRes.data);
      setStats(statsRes.data);
      setFollowups(followupsRes.data);
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddJob = async () => {
    if (!newJob.company || !newJob.position) {
      toast.error('Please fill in company and position');
      return;
    }

    try {
      const response = await pipelineAPI.create(newJob);
      setJobs([response.data.job, ...jobs]);
      setNewJob({
        company: '', position: '', location: '', salary_range: '',
        job_url: '', job_description: '', status: 'saved', priority: 'medium',
        notes: '', cv_used: ''
      });
      setDialogOpen(false);
      toast.success('Job added to pipeline!');
      loadData(); // Refresh stats
    } catch (error) {
      toast.error('Failed to add job');
    }
  };

  const handleUpdateStatus = async (jobId, status) => {
    try {
      await pipelineAPI.updateStatus(jobId, status);
      setJobs(jobs.map(j => j.id === jobId ? { ...j, status } : j));
      toast.success('Status updated!');
      loadData(); // Refresh stats
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Delete this job from your pipeline?')) return;
    try {
      await pipelineAPI.delete(jobId);
      setJobs(jobs.filter(j => j.id !== jobId));
      toast.success('Job removed');
      loadData();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleAddContact = async (jobId) => {
    if (!newContact.name) {
      toast.error('Please enter contact name');
      return;
    }
    try {
      await pipelineAPI.addContact(jobId, newContact);
      // Refresh the detail view
      const updatedJob = await pipelineAPI.get(jobId);
      setDetailJob(updatedJob.data);
      setJobs(jobs.map(j => j.id === jobId ? updatedJob.data : j));
      setNewContact({ name: '', title: '', email: '', phone: '', linkedin: '', notes: '' });
      toast.success('Contact added!');
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const getQuickScore = async (job) => {
    if (!job.cv_used || !job.job_description) {
      toast.error('Need CV and job description to calculate match');
      return;
    }
    try {
      const response = await jobMatchAPI.quickScore({
        cv_id: job.cv_used,
        job_title: job.position,
        company_name: job.company,
        job_description: job.job_description
      });
      // Update the job with match score
      const updatedJobs = jobs.map(j => 
        j.id === job.id ? { ...j, match_score: response.data.match_score } : j
      );
      setJobs(updatedJobs);
      toast.success(`Match Score: ${response.data.match_score}%`);
    } catch (error) {
      toast.error('Failed to calculate match score');
    }
  };

  const filteredJobs = filterStatus === 'all' 
    ? jobs 
    : jobs.filter(j => j.status === filterStatus);

  const jobsByStatus = Object.entries(statusConfig)
    .sort(([,a], [,b]) => a.order - b.order)
    .map(([status, config]) => ({
      status,
      ...config,
      jobs: jobs.filter(j => j.status === status)
    }));

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Subscription Required</h1>
          <p className="text-slate-600 mb-6">Subscribe to access the Job Pipeline</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-600"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="h-6 w-px bg-slate-200"></div>
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-500" />
            <h1 className="font-semibold text-slate-900">Job Pipeline</h1>
          </div>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-pipeline-job-btn" className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Add Job
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Job to Pipeline</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company *</Label>
                  <Input
                    data-testid="pipeline-company-input"
                    value={newJob.company}
                    onChange={(e) => setNewJob({ ...newJob, company: e.target.value })}
                    placeholder="e.g., Shoprite"
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label>Position *</Label>
                  <Input
                    data-testid="pipeline-position-input"
                    value={newJob.position}
                    onChange={(e) => setNewJob({ ...newJob, position: e.target.value })}
                    placeholder="e.g., Sales Assistant"
                    className="mt-1.5 h-11"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Location</Label>
                  <Input
                    value={newJob.location}
                    onChange={(e) => setNewJob({ ...newJob, location: e.target.value })}
                    placeholder="e.g., Cape Town"
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label>Salary Range</Label>
                  <Input
                    value={newJob.salary_range}
                    onChange={(e) => setNewJob({ ...newJob, salary_range: e.target.value })}
                    placeholder="e.g., R15,000 - R20,000"
                    className="mt-1.5 h-11"
                  />
                </div>
              </div>

              <div>
                <Label>Job URL</Label>
                <Input
                  value={newJob.job_url}
                  onChange={(e) => setNewJob({ ...newJob, job_url: e.target.value })}
                  placeholder="https://careers24.com/job/..."
                  className="mt-1.5 h-11"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={newJob.status} onValueChange={(v) => setNewJob({ ...newJob, status: v })}>
                    <SelectTrigger className="mt-1.5 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusConfig).map(([value, config]) => (
                        <SelectItem key={value} value={value}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newJob.priority} onValueChange={(v) => setNewJob({ ...newJob, priority: v })}>
                    <SelectTrigger className="mt-1.5 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(priorityConfig).map(([value, config]) => (
                        <SelectItem key={value} value={value}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>CV to Use</Label>
                <Select value={newJob.cv_used} onValueChange={(v) => setNewJob({ ...newJob, cv_used: v })}>
                  <SelectTrigger className="mt-1.5 h-11">
                    <SelectValue placeholder="Select CV (for matching)" />
                  </SelectTrigger>
                  <SelectContent>
                    {cvs.map((cv) => (
                      <SelectItem key={cv.id} value={cv.id}>
                        {cv.personal_details?.full_name || cv.title || 'Untitled CV'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Job Description</Label>
                <Textarea
                  value={newJob.job_description}
                  onChange={(e) => setNewJob({ ...newJob, job_description: e.target.value })}
                  placeholder="Paste the job description for matching..."
                  className="mt-1.5 min-h-[100px]"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={newJob.notes}
                  onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
                  placeholder="Any notes about this opportunity..."
                  className="mt-1.5 min-h-[60px]"
                />
              </div>

              <Button 
                data-testid="save-pipeline-job-btn"
                onClick={handleAddJob} 
                className="w-full h-11 bg-slate-900 hover:bg-slate-800"
              >
                Add to Pipeline
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Stats Bar */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm text-slate-500">Applied</p>
                <p className="text-2xl font-bold text-blue-600">{stats.by_status?.applied || 0}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm text-slate-500">Interviewing</p>
                <p className="text-2xl font-bold text-amber-600">
                  {(stats.by_status?.phone_screen || 0) + (stats.by_status?.interview || 0)}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm text-slate-500">Offers</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.by_status?.offer || 0}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm text-slate-500">Interview Rate</p>
                <p className="text-2xl font-bold text-purple-600">{stats.interview_rate || 0}%</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm text-slate-500">This Week</p>
                <p className="text-2xl font-bold text-slate-900">{stats.applications_this_week || 0}</p>
              </div>
            </div>
          )}

          {/* Follow-up Alerts */}
          {followups.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-900">Follow-ups Needed ({followups.length})</h3>
              </div>
              <div className="space-y-2">
                {followups.slice(0, 3).map((job) => (
                  <div key={job.id} className="flex items-center justify-between bg-white rounded-lg p-3">
                    <div>
                      <p className="font-medium text-slate-900">{job.position} at {job.company}</p>
                      <p className="text-sm text-amber-600">{job.followup_reason}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setDetailJob(job)}
                    >
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'pipeline' ? 'default' : 'outline'}
              onClick={() => setActiveTab('pipeline')}
              className={activeTab === 'pipeline' ? 'bg-slate-900' : ''}
            >
              Pipeline View
            </Button>
            <Button
              variant={activeTab === 'list' ? 'default' : 'outline'}
              onClick={() => setActiveTab('list')}
              className={activeTab === 'list' ? 'bg-slate-900' : ''}
            >
              List View
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Your Pipeline is Empty</h2>
              <p className="text-slate-600 mb-6">Start tracking jobs you're interested in</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Job
              </Button>
            </div>
          ) : activeTab === 'pipeline' ? (
            /* Kanban Pipeline View */
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-4 min-w-max">
                {jobsByStatus.filter(s => s.jobs.length > 0 || ['saved', 'applied', 'interview', 'offer'].includes(s.status)).map((statusGroup) => (
                  <div key={statusGroup.status} className="w-72 flex-shrink-0">
                    <div className={`rounded-t-xl px-4 py-3 ${statusGroup.color}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {statusGroup.icon}
                          <span className="font-medium">{statusGroup.label}</span>
                        </div>
                        <span className="text-sm font-bold">{statusGroup.jobs.length}</span>
                      </div>
                    </div>
                    <div className="bg-slate-100 rounded-b-xl p-2 min-h-[300px] space-y-2">
                      {statusGroup.jobs.map((job) => (
                        <div 
                          key={job.id}
                          className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => setDetailJob(job)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-medium text-slate-900 text-sm">{job.position}</h4>
                              <p className="text-xs text-slate-500">{job.company}</p>
                            </div>
                            {job.match_score && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                job.match_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                                job.match_score >= 50 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {job.match_score}%
                              </span>
                            )}
                          </div>
                          {job.location && (
                            <div className="flex items-center gap-1 text-xs text-slate-400">
                              <MapPin className="w-3 h-3" />
                              {job.location}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                            <span className={`text-xs px-2 py-0.5 rounded ${priorityConfig[job.priority]?.color || ''}`}>
                              {priorityConfig[job.priority]?.label}
                            </span>
                            {job.contacts?.length > 0 && (
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {job.contacts.length}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* List View */
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-semibold text-slate-900">All Applications ({filteredJobs.length})</h2>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {Object.entries(statusConfig).map(([value, config]) => (
                      <SelectItem key={value} value={value}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="divide-y divide-slate-200">
                {filteredJobs.map((job) => {
                  const status = statusConfig[job.status] || statusConfig.saved;
                  return (
                    <div 
                      key={job.id}
                      className="p-4 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                      onClick={() => setDetailJob(job)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Building className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{job.position}</h3>
                          <p className="text-sm text-slate-500">{job.company} {job.location && `• ${job.location}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {job.match_score && (
                          <span className={`text-sm px-2 py-1 rounded-full ${
                            job.match_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                            job.match_score >= 50 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {job.match_score}% match
                          </span>
                        )}
                        <span className={`text-sm px-3 py-1 rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Detail Drawer */}
      <Dialog open={!!detailJob} onOpenChange={() => setDetailJob(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailJob && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{detailJob.position}</DialogTitle>
                    <p className="text-slate-500 mt-1">{detailJob.company}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {detailJob.job_url && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={detailJob.job_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View Job
                        </a>
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => { handleDeleteJob(detailJob.id); setDetailJob(null); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Status & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={detailJob.status} 
                      onValueChange={(v) => { handleUpdateStatus(detailJob.id, v); setDetailJob({...detailJob, status: v}); }}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(statusConfig).map(([value, config]) => (
                          <SelectItem key={value} value={value}>{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Match Score</Label>
                    <div className="mt-1.5 flex items-center gap-2">
                      {detailJob.match_score ? (
                        <span className={`text-lg font-bold px-3 py-1 rounded-lg ${
                          detailJob.match_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                          detailJob.match_score >= 50 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {detailJob.match_score}%
                        </span>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => getQuickScore(detailJob)}>
                          <Target className="w-4 h-4 mr-1" />
                          Calculate Match
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
                  {detailJob.location && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{detailJob.location}</span>
                    </div>
                  )}
                  {detailJob.salary_range && (
                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="w-4 h-4 text-slate-400" />
                      <span>{detailJob.salary_range}</span>
                    </div>
                  )}
                  {detailJob.applied_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span>Applied: {new Date(detailJob.applied_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {detailJob.notes && (
                  <div>
                    <Label>Notes</Label>
                    <p className="mt-1.5 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{detailJob.notes}</p>
                  </div>
                )}

                {/* Contacts */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Contacts ({detailJob.contacts?.length || 0})</Label>
                  </div>
                  
                  {detailJob.contacts?.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {detailJob.contacts.map((contact, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                          <p className="font-medium text-slate-900">{contact.name}</p>
                          {contact.title && <p className="text-sm text-slate-500">{contact.title}</p>}
                          <div className="flex gap-4 mt-2 text-xs text-slate-500">
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-blue-600">
                                <Mail className="w-3 h-3" /> {contact.email}
                              </a>
                            )}
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {contact.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Contact Form */}
                  <div className="p-4 border border-dashed border-slate-300 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 mb-3">Add Contact</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Name"
                        value={newContact.name}
                        onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                        className="h-9"
                      />
                      <Input
                        placeholder="Title"
                        value={newContact.title}
                        onChange={(e) => setNewContact({...newContact, title: e.target.value})}
                        className="h-9"
                      />
                      <Input
                        placeholder="Email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                        className="h-9"
                      />
                      <Input
                        placeholder="Phone"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                        className="h-9"
                      />
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => handleAddContact(detailJob.id)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Contact
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobPipelinePage;
